import { ChangeDetectionStrategy, Component, ComponentFactoryResolver, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { DynamicComponentHost } from '../../modules/dynamic-components/dynamic-component-host.component';
import { DynamicComponentHostDirective } from '../../modules/dynamic-components/dynamic-component-host.directive';
import { InfoBoxTab } from './info-box-tab.model';
import { NavItem } from './nav-item.model';

@Component({
  selector: 'rek-info-box',
  templateUrl: './info-box.component.html',
  styleUrls: ['./info-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoBoxComponent extends DynamicComponentHost implements OnInit, OnChanges {
  @Input() readonly title: string;
  @Input() readonly infoBoxTabs: InfoBoxTab[];
  @Input() shouldShowTabs = false;

  navItems: NavItem[] = [];

  @ViewChild(DynamicComponentHostDirective, { static: true }) private readonly dynamicComponentHost: DynamicComponentHostDirective;

  constructor(protected componentFactoryResolver: ComponentFactoryResolver) {
    super(componentFactoryResolver);
  }

  ngOnInit() {
    this.init();
  }

  ngOnChanges() {
    this.init();
  }


  selectNavItem(navItem: NavItem, forceRender: boolean = false): void {
    if (!navItem.isActive || forceRender) {
      const tab = this.getActiveTab();

      if (tab && navItem && tab.navItems.includes(navItem)) {
        this.setActiveAndDeactivateOthers(tab.navItems, navItem);
        super.renderComponentForItem(navItem.componentToRenderData);
      }
    }
  }

  changeTab(tab: InfoBoxTab): void {
    if (tab !== this.getActiveTab()) {
      const possiblyActiveNavItem = tab.navItems.find(n => n.isActive);

      this.setActiveAndDeactivateOthers(this.infoBoxTabs, tab);
      this.selectNavItem(possiblyActiveNavItem ?? tab.navItems[0], true);
      this.navItems = tab.navItems;
    }
  }

  private init(): void {
    this.handleErrors();

    this.initSelectedTab();
    this.initTabsVisibility();

    super.initHost(this.dynamicComponentHost);
    this.renderInitialItem();
  }

  private handleErrors(): void {
    if (!this.title ||
      !this.infoBoxTabs ||
      this.infoBoxTabs.length === 0 ||
      this.infoBoxTabs.some(i => !i.navItems || i.navItems.length === 0)) {
      throw new Error('Check passed input parameters. All of them must be supplied.');
    }
  }

  private initSelectedTab(): void {
    let preinitializedActiveTab: InfoBoxTab = this.getActiveTab();

    if (!preinitializedActiveTab) {
      preinitializedActiveTab = this.infoBoxTabs[0];
      preinitializedActiveTab.isActive = true;
    }

    this.navItems = preinitializedActiveTab.navItems;
  }

  private initTabsVisibility(): void {
    if (this.infoBoxTabs.length > 1) {
      this.shouldShowTabs = true;
    }
  }

  private renderInitialItem(): void {
    const activeTab: InfoBoxTab = this.getActiveTab();
    let activeNavItem: NavItem = activeTab.navItems.find(n => n.isActive);

    if (!activeNavItem) {
      activeNavItem = activeTab.navItems[0];
      activeNavItem.isActive = true;
    }

    super.renderComponentForItem(activeNavItem.componentToRenderData);
  }

  private setActiveAndDeactivateOthers(items: any[], item: any): void {
    items.forEach(n => {
      n.isActive = n === item;
    });
  }

  private getActiveTab(): InfoBoxTab {
    return this.infoBoxTabs.find(t => t.isActive);
  }
}
